import React, { FC, useCallback } from 'react';
import debounce from 'debounce-promise';
import memoize from 'memoizee';

import { useAutocompleteUsersHook, useGetUsersByIdsHook, User, UserSummaryDTO } from '@/api/generated';
import DropdownSelect, { DropdownSelectOption, DropdownSelectProps } from '@/components/fields/DropdownSelect';
import { UserAvatar } from '@/components/UserAvatar';
import { getUserFullName } from '@/utils/user';

const AVATAR_SIZE = 20;
const DEBOUNCE_DELAY = 300;

type ClientUserDropdownSelectProps = Omit<DropdownSelectProps, 'options'> & {
  organizationSlug?: string;
};

const filterBySearch = (options: DropdownSelectOption[], inputValue?: string) => {
  if (!inputValue) return options;

  return options.filter(option => (option.label || '').toLowerCase().includes(inputValue?.toLowerCase() || ''));
};

const userToOption = (user: User) => ({
  label: getUserFullName(user),
  value: user.id!,
  Icon: <UserAvatar user={user} size={AVATAR_SIZE} sx={{ ml: 0.5, mr: 0.5 }} />
});

const ClientUserDropdownSelect: FC<ClientUserDropdownSelectProps> = props => {
  const getAutocompletionUsers = useAutocompleteUsersHook();
  const getUsersByIds = useGetUsersByIdsHook();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptions = useCallback(
    memoize(
      async (inputValue?: string) => {
        const usersData = await getAutocompletionUsers({ q: inputValue, organizationId: props.organizationSlug });

        const newUserOptions = (usersData?.userSummaries || []).map((userSummary: UserSummaryDTO) =>
          userToOption(userSummary as User)
        );

        return filterBySearch(newUserOptions, inputValue);
      },
      { promise: true }
    ),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptionsDebounced = useCallback(debounce(loadUserOptions, DEBOUNCE_DELAY), [loadUserOptions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptionsByIds = useCallback(
    memoize(
      async (ids?: string[]) => {
        const users = await getUsersByIds({ id: ids! });
        const newUserOptions = (users || []).map((user: User) => userToOption(user));

        return newUserOptions;
      },
      { promise: true }
    ),
    []
  );

  return (
    <DropdownSelect {...props} loadOptions={loadUserOptionsDebounced} loadSelectedOptions={loadUserOptionsByIds} />
  );
};

export default ClientUserDropdownSelect;
